<template>
  <section class="section">
    <div class="dashboard-card">
      <div class="dashboard-header">
        <div>
          <h1>Company Dashboard</h1>
          <p class="muted">Welcome, {{ displayName }}</p>
        </div>

        <span class="status-badge" :class="subscriptionClass">
          {{ subscriptionLabel }}
        </span>
      </div>

      <div v-if="error" class="alert error">
        {{ error }}
      </div>

      <div v-if="!isActive" class="subscription-box">
        <h2>Activate your subscription</h2>
        <p>
          Your company account has been created. Choose your subscription to
          unlock employee registration, time tracking, timesheets, and reports.
        </p>

        <div class="plan-summary">
          <strong>Selected plan:</strong>
          <span>{{ planLabel }}</span>
        </div>

        <button class="button" :disabled="loading" @click="startCheckout">
          {{ loading ? 'Opening checkout...' : 'Subscribe now' }}
        </button>
      </div>

      <div v-else class="dashboard-grid">
        <div class="card">
          <h2>Employees</h2>
          <p>Add and manage employees for your company.</p>
          <RouterLink class="button secondary" to="/operationsdashboard">
            Manage employees
          </RouterLink>
        </div>

        <div class="card">
          <h2>Billing</h2>
          <p>Manage your subscription, payment method, and invoices.</p>
          <button class="button secondary" :disabled="billingLoading" @click="openBillingPortal">
            {{ billingLoading ? 'Opening...' : 'Manage billing' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import axios from 'axios'
import { computed, ref } from 'vue'
import { BASE_URL } from '@/config';

const API_URL = BASE_URL

const loading = ref(false)
const billingLoading = ref(false)
const error = ref('')

const storedUser = localStorage.getItem('user')
const user = ref(storedUser ? JSON.parse(storedUser) : null)


const jwt = localStorage.getItem('jwt')

const displayName = computed(() => {
  return user.value?.fullname || user.value?.username || user.value?.email || 'Admin'
})

const subscriptionStatus = computed(() => {
  return user.value?.subscriptionstatus || 'pending'
})

const isActive = computed(() => {
  return subscriptionStatus.value === 'active'
})

const subscriptionLabel = computed(() => {
  if (subscriptionStatus.value === 'active') return 'Active'
  if (subscriptionStatus.value === 'past_due') return 'Past due'
  if (subscriptionStatus.value === 'cancelled') return 'Cancelled'
  return 'Pending'
})

const subscriptionClass = computed(() => {
  return subscriptionLabel.value.toLowerCase().replace(' ', '-')
})

const planLabel = computed(() => {
  if (user.value?.plan === 'growth') return 'Growth — $4 per employee/month'
  return 'Starter — $19/month'
})

async function startCheckout() {
  error.value = ''

  if (!jwt) {
    error.value = 'Please log in again before subscribing.'
    return
  }

  loading.value = true

  try {
    const response = await axios.post(
      `${API_URL}/api/billing-checkout`,
      {},
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      }
    )

    if (!response.data?.checkoutUrl) {
      throw new Error('Checkout URL was not returned.')
    }

    window.location.href = response.data.checkoutUrl
  } catch (err) {
    error.value =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message ||
      'Unable to start checkout.'
  } finally {
    loading.value = false
  }
}

async function openBillingPortal() {
  error.value = ''

  if (!jwt) {
    error.value = 'Please log in again before managing billing.'
    return
  }

  billingLoading.value = true

  try {
    const response = await axios.post(
      `${API_URL}/api/billing-portal`,
      {},
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      }
    )

    if (!response.data?.url) {
      throw new Error('Billing portal URL was not returned.')
    }

    window.location.href = response.data.url
  } catch (err) {
    error.value =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message ||
      'Unable to open billing portal.'
  } finally {
    billingLoading.value = false
  }
}
</script>
<style scoped>

/* Layout */
.section {
  padding: 2rem 1rem;
}

.dashboard-card {
  max-width: 1000px;
  margin: 0 auto;
}

/* Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.dashboard-header h1 {
  margin: 0;
  font-size: 1.8rem;
}

.muted {
  color: #6b7280;
  font-size: 0.9rem;
}

/* Status badge */
.status-badge {
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.8rem;
  background: #e5e7eb;
  color: #374151;
}

.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.past-due,
.status-badge.cancelled {
  background: #fee2e2;
  color: #991b1b;
}

/* Subscription box */
.subscription-box {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
}

.subscription-box h2 {
  margin-bottom: 0.5rem;
}

.plan-summary {
  margin: 1rem 0;
  font-size: 0.95rem;
  color: #374151;
}

/* Grid */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

/* Cards */
.card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.5rem;
  transition: all 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

.card h2 {
  margin-bottom: 0.5rem;
}

/* Buttons */
.button {
  display: inline-block;
  padding: 0.65rem 1rem;
  border-radius: 0.5rem;
  background: #111827;
  color: white;
  text-decoration: none;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.button:hover {
  background: #1f2937;
}

.button.secondary {
  background: #e5e7eb;
  color: #111827;
}

.button.secondary:hover {
  background: #d1d5db;
}

/* Alerts */
.alert.error {
  background: #fee2e2;
  color: #991b1b;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
}

/* Responsive */
@media (max-width: 900px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

</style>

